import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Video, Building2, Phone, FileText } from "lucide-react";
import { format } from "date-fns";

interface ClientSessionHistoryProps {
  clientId: string;
  clinicianId: string;
}

export function ClientSessionHistory({ clientId, clinicianId }: ClientSessionHistoryProps) {
  const { data: sessions, isLoading } = useQuery({
    queryKey: ['client-session-history', clientId, clinicianId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('therapy_sessions')
        .select('*')
        .eq('client_id', clientId)
        .eq('clinician_id', clinicianId)
        .order('session_date', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const getLocationIcon = (locationType: string) => {
    switch (locationType) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'in_person': return <Building2 className="h-4 w-4" />;
      case 'phone': return <Phone className="h-4 w-4" />;
      default: return <MapPin className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Completed</Badge>;
      case 'confirmed':
        return <Badge className="bg-primary/10 text-primary border-primary/20">Confirmed</Badge>;
      case 'scheduled':
        return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Scheduled</Badge>;
      case 'cancelled':
        return <Badge className="bg-destructive/10 text-destructive border-destructive/20">Cancelled</Badge>;
      case 'no_show':
        return <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">No Show</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const upcomingSessions = sessions?.filter(s => 
    new Date(s.session_date) >= new Date() && s.status !== 'cancelled'
  ) || [];

  const pastSessions = sessions?.filter(s => 
    new Date(s.session_date) < new Date() || s.status === 'completed'
  ) || [];

  if (isLoading) {
    return (
      <Card className="bg-card/50 border-border/30">
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upcoming Sessions */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Upcoming Sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingSessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No upcoming sessions scheduled</p>
              <p className="text-sm mt-1">Contact your practitioner to book a session</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingSessions.map(session => (
                <div 
                  key={session.id}
                  className="p-4 rounded-lg bg-primary/5 border border-primary/20"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-foreground">{session.session_type}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(session.session_date), 'EEEE, MMMM d, yyyy')}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {session.session_time?.slice(0, 5)} ({session.duration_minutes} min)
                      </div>
                    </div>
                    {getStatusBadge(session.status || 'scheduled')}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {getLocationIcon(session.location_type)}
                    <span className="capitalize">{session.location_type.replace('_', ' ')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session History */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Session History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pastSessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No past sessions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pastSessions.slice(0, 10).map(session => (
                <div 
                  key={session.id}
                  className="p-3 rounded-lg bg-muted/30 border border-border/30"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-center min-w-[60px]">
                        <p className="text-sm font-medium text-foreground">
                          {format(new Date(session.session_date), 'MMM d')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(session.session_date), 'yyyy')}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-foreground text-sm">{session.session_type}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {getLocationIcon(session.location_type)}
                          <span>{session.duration_minutes} min</span>
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(session.status || 'completed')}
                  </div>
                  {session.session_notes && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2 italic">
                      {session.session_notes}
                    </p>
                  )}
                </div>
              ))}
              {pastSessions.length > 10 && (
                <p className="text-center text-sm text-muted-foreground">
                  Showing 10 of {pastSessions.length} sessions
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
